// Keep every group under the same keyed parent while the split tree changes.
// Wrapping an existing group in a new SplitContainer unmounted its MemoryRouter,
// which reset the page, its scroll position, and its local navigation history.

import { useCallback, useRef, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { tabsStore } from '@/stores/TabsStore';
import type { PaneNode, TabGroupNode } from '@/features/tabs/types';
import { TAB_DRAG_TYPE } from '@/features/tabs/logic';
import { TabStrip } from './TabStrip';
import { TabPane } from './TabPane';

type Bounds = { left: number; top: number; width: number; height: number };
type GroupLayout = { group: TabGroupNode; bounds: Bounds };
type HandleLayout = { id: string; direction: 'row' | 'column'; ratio: number; bounds: Bounds };

function collectLayout(node: PaneNode, bounds: Bounds, groups: GroupLayout[], handles: HandleLayout[]) {
  if (node.kind === 'group') {
    groups.push({ group: node, bounds });
    return;
  }
  handles.push({ id: node.id, direction: node.direction, ratio: node.ratio, bounds });
  if (node.direction === 'row') {
    const firstWidth = bounds.width * node.ratio;
    collectLayout(node.children[0], { ...bounds, width: firstWidth }, groups, handles);
    collectLayout(node.children[1], {
      ...bounds, left: bounds.left + firstWidth, width: bounds.width - firstWidth,
    }, groups, handles);
  } else {
    const firstHeight = bounds.height * node.ratio;
    collectLayout(node.children[0], { ...bounds, height: firstHeight }, groups, handles);
    collectLayout(node.children[1], {
      ...bounds, top: bounds.top + firstHeight, height: bounds.height - firstHeight,
    }, groups, handles);
  }
}

const EDGES = [
  { edge: 'left' as const, direction: 'row' as const, before: true, cls: 'left-0 top-0 bottom-0 w-1/4' },
  { edge: 'right' as const, direction: 'row' as const, before: false, cls: 'right-0 top-0 bottom-0 w-1/4' },
  { edge: 'top' as const, direction: 'column' as const, before: true, cls: 'top-0 left-0 right-0 h-1/4' },
  { edge: 'bottom' as const, direction: 'column' as const, before: false, cls: 'bottom-0 left-0 right-0 h-1/4' },
];

const GroupView = observer(({ group, reportPaneLocation }: { group: TabGroupNode; reportPaneLocation: (tabId: string, path: string, replace: boolean) => void }) => {
  const [dragging, setDragging] = useState(false);
  const [hotEdge, setHotEdge] = useState<string | null>(null);
  const tabs = tabsStore.tabsOf(group.id);
  const isFocused = tabsStore.focusedGroupId === group.id;

  return (
    <div
      className="relative flex flex-col flex-1 min-w-0 min-h-0"
      onFocusCapture={() => {
        if (!isFocused) tabsStore.focusGroup(group.id);
      }}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes(TAB_DRAG_TYPE)) setDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) {
          setDragging(false);
          setHotEdge(null);
        }
      }}
      onDrop={() => {
        setDragging(false);
        setHotEdge(null);
      }}
    >
      <TabStrip group={group} />
      <div className="relative flex flex-col flex-1 min-h-0">
        {tabs.map((tab) =>
          tabsStore.isLive(tab.id) ? (
            <TabPane
              key={tab.id}
              tabId={tab.id}
              path={tab.path}
              isActive={group.activeTabId === tab.id}
              isFocused={isFocused}
              width={group.width}
              align={group.align}
              reportPaneLocation={reportPaneLocation}
            />
          ) : null,
        )}
        {tabs.length === 0 && (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">&nbsp;</div>
        )}
        {dragging && EDGES.map((zone) => (
          <div
            key={zone.edge}
            className={`absolute z-30 ${zone.cls} ${hotEdge === zone.edge ? 'bg-primary/20 border-2 border-primary' : ''}`}
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes(TAB_DRAG_TYPE)) return;
              e.preventDefault();
              e.stopPropagation();
              setHotEdge(zone.edge);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const tabId = e.dataTransfer.getData(TAB_DRAG_TYPE);
              setDragging(false);
              setHotEdge(null);
              if (!tabId) return;
              const holder = tabsStore.tabsOf(group.id).some((tb) => tb.id === tabId);
              if (!holder) tabsStore.reorderOrMove(tabId, group.id, tabsStore.tabsOf(group.id).length);
              tabsStore.splitWithTab(group.id, tabId, zone.direction, zone.before);
            }}
          />
        ))}
      </div>
    </div>
  );
});

function SplitHandle({ layout, workspaceRef }: {
  layout: HandleLayout;
  workspaceRef: React.RefObject<HTMLDivElement | null>;
}) {
  const { t } = useI18n();
  const isRow = layout.direction === 'row';
  const startDrag = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const workspace = workspaceRef.current;
    if (!workspace) return;
    const box = workspace.getBoundingClientRect();
    const left = box.left + box.width * layout.bounds.left / 100;
    const top = box.top + box.height * layout.bounds.top / 100;
    const width = box.width * layout.bounds.width / 100;
    const height = box.height * layout.bounds.height / 100;
    const onMove = (e: MouseEvent) => {
      const ratio = isRow ? (e.clientX - left) / width : (e.clientY - top) / height;
      tabsStore.setRatio(layout.id, ratio);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = isRow ? 'col-resize' : 'row-resize';
  }, [isRow, layout, workspaceRef]);

  const { left, top, width, height } = layout.bounds;
  return (
    <div
      role="slider"
      tabIndex={0}
      aria-orientation={isRow ? 'vertical' : 'horizontal'}
      aria-valuenow={Math.round(layout.ratio * 100)}
      aria-valuemin={15}
      aria-valuemax={85}
      aria-label={t('layout.tabs.resize')}
      onMouseDown={startDrag}
      onKeyDown={(e) => {
        const less = isRow ? 'ArrowLeft' : 'ArrowUp';
        const more = isRow ? 'ArrowRight' : 'ArrowDown';
        if (e.key !== less && e.key !== more) return;
        e.preventDefault();
        tabsStore.setRatio(layout.id, layout.ratio + (e.key === more ? 0.02 : -0.02));
      }}
      style={isRow ? {
        left: `${left + width * layout.ratio}%`, top: `${top}%`, height: `${height}%`,
        transform: 'translateX(-50%)',
      } : {
        left: `${left}%`, top: `${top + height * layout.ratio}%`, width: `${width}%`,
        transform: 'translateY(-50%)',
      }}
      className={`absolute z-20 bg-border hover:bg-primary/40 focus:bg-primary/60 focus:outline-none ${
        isRow ? 'w-1 cursor-col-resize' : 'h-1 cursor-row-resize'
      }`}
    />
  );
}

export const PaneView = observer(({ node, reportPaneLocation }: { node: PaneNode; reportPaneLocation: (tabId: string, path: string, replace: boolean) => void }) => {
  const workspaceRef = useRef<HTMLDivElement>(null);
  const groups: GroupLayout[] = [];
  const handles: HandleLayout[] = [];
  collectLayout(node, { left: 0, top: 0, width: 100, height: 100 }, groups, handles);

  return (
    <div ref={workspaceRef} className="relative flex flex-1 min-w-0 min-h-0">
      {groups.map(({ group, bounds }) => (
        <div
          key={group.id}
          className="absolute flex min-w-0 min-h-0 overflow-hidden"
          style={{
            left: `${bounds.left}%`, top: `${bounds.top}%`,
            width: `${bounds.width}%`, height: `${bounds.height}%`,
          }}
        >
          <GroupView group={group} reportPaneLocation={reportPaneLocation} />
        </div>
      ))}
      {handles.map((layout) => <SplitHandle key={layout.id} layout={layout} workspaceRef={workspaceRef} />)}
    </div>
  );
});
